import { forwardRef } from "react";
import { useSelectedStudentIdContext } from "#/components/exam-results/context/studentsList/context";
import { useParams, useSearchParams } from "@monta/router-react";
import { getMainExamResultset } from "../../services";
import {
  dayjs,
  LoadingSpinner,
  RTooltip,
  Tab,
  Tabs,
  ToolCard,
  useBreakLG
} from "@monta/design-system";
import { convertModuleInfosToStackedBarChartData } from "#/components/exam-results/views/type-2/utils";
import {
  HorizontalStackedBarChart,
  SegmentedGauge,
  type SegmentedGaugeSegment
} from "@monta/components";
import {
  DEFAULT_STATUS,
  STATUS_CONFIG
} from "../../components/summary-info/status-config";
import DetailsBox from "../../components/details-box";
import {
  transformExamDataToDifficultyTableData,
  transformExamDataToTableData
} from "../../lib/helper";
import DifficultyTable from "../../components/difficulty-table";
import DetailsAccordion from "../../components/details-accordion";
import UserInfo from "../../components/user-info";
import { QuestionLinear } from "@monta/icons";
import TableTooltip from "../../components/table-tooltip";
import SummaryInfo from "../../components/summary-info";
import GaugeChart from "../../components/summary-info/index.mobile";
import InfoDetailsTable from "../../components/details-table";
import {
  DETAILS_TAB_QUERY_KEY,
  SUMMARY_INFO_TOOLTIP_CONTENT
} from "../../lib/constants";
import MsgBox from "../../components/msg-box";
import MultiLessonTabs from "../../components/Tabs";

const Type2 = forwardRef<HTMLDivElement, { isPrint?: boolean }>(
  ({ isPrint }, ref) => {
    const params = useParams();

    const [searchParams, setSearchParams] = useSearchParams();

    const { selectedStudentId } = useSelectedStudentIdContext();

    const examTakingId = Number(params["examTakingId"]);
    const isSelfExam = searchParams.get("self-exam");

    const activeDetailsTabId = Number(
      searchParams.get(DETAILS_TAB_QUERY_KEY) ?? 1
    );
    const isDesktop = useBreakLG();

    const { data, isFetching } = getMainExamResultset.useQuery({
      request: {
        body: {
          examTakingDetailId: examTakingId,
          studentId: selectedStudentId
        }
      },
      queryOptions: {
        enabled: selectedStudentId > 0
      }
    });

    const isSingleLesson = data?.moduleNo === 1;

    // TODO: add skeleton loading
    if (isFetching) {
      return (
        <div className="flex flex-col items-center justify-center gap-2">
          <p className="text-center text-2xl text-neutral-600 font-semibold">
            در حال بارگذاری...
          </p>

          <LoadingSpinner width={24} height={24} color="#2b7fff" />
        </div>
      );
    }

    // TODO: Handle when there is no data
    if (!data) {
      return (
        <div className="flex flex-col items-center justify-center gap-2">
          <p className="text-center text-2xl text-neutral-600 font-semibold">
            موردی برای نمایش وجود نداردو
          </p>
        </div>
      );
    }

    const {
      overalInfoJson,
      moduleInfos,
      moduleGroupInfos,
      difficultyLevelInfos,
      chapterInfos
    } = data;
    const getTableTitle = () => {
      if (isSingleLesson) return "جزئیات به تفکیک موضوع";

      return activeDetailsTabId === 1
        ? "جزئیات به تفکیک کتاب"
        : "جزئیات به تفکیک گروه درسی";
    };

    const getStackedBarTitle = () => {
      if (isSingleLesson) return "مقایسه نمره موضوعات";

      return activeDetailsTabId === 1
        ? "مقایسه نمره کتب"
        : "مقایسه نمره گروه‌ درسی";
    };

    const isGroupTab = !isSingleLesson && activeDetailsTabId !== 1;
    const stackedSource = isGroupTab ? moduleGroupInfos : moduleInfos;
    const stackedData = isSingleLesson
      ? convertModuleInfosToStackedBarChartData(chapterInfos ?? [])
      : convertModuleInfosToStackedBarChartData(stackedSource) || [];

    const totalQuestions =
      difficultyLevelInfos?.reduce((sum, item) => {
        return sum + item.corrects + item.inCorrects + item.noAnswers;
      }, 0) ?? 0;

    const calculateSegmentedGaugeSegments = (): SegmentedGaugeSegment[] => {
      const segmentMap = new Map<string, number>();

      difficultyLevelInfos?.forEach((item) => {
        const total = item.corrects + item.inCorrects + item.noAnswers;
        const percentage = (total / totalQuestions) * 100;
        let key = "";

        if (
          item.levelName.includes("آسان") ||
          item.levelName.includes("ساده")
        ) {
          key = "low";
        } else if (
          item.levelName.includes("متوسط") ||
          item.levelName.includes("معمولی")
        ) {
          key = "mid";
        } else if (
          item.levelName.includes("بیش") ||
          item.levelName.includes("سخت") ||
          item.levelName.includes("دشوار")
        ) {
          key = "high";
        } else {
          // Default to medium if we can't determine the level
          key = "mid";
        }

        // If this key already exists, add to it
        if (segmentMap.has(key)) {
          segmentMap.set(key, segmentMap.get(key)! + percentage);
        } else {
          segmentMap.set(key, percentage);
        }
      });
      const segments: SegmentedGaugeSegment[] = [];

      if (segmentMap.has("low")) {
        segments.push({
          key: "low",
          label: "آسان",
          color: "#039855",
          weight: Math.round(segmentMap.get("low")! * 100) / 100
        });
      }

      if (segmentMap.has("mid")) {
        segments.push({
          key: "mid",
          label: "متوسط",
          color: "#FF6A00",
          weight: Math.round(segmentMap.get("mid")! * 100) / 100
        });
      }

      if (segmentMap.has("high")) {
        segments.push({
          key: "high",
          label: "سخت",
          color: "#D92D20",
          weight: Math.round(segmentMap.get("high")! * 100) / 100
        });
      }

      return segments;
    };
    const segmentedGaugeSegments = calculateSegmentedGaugeSegments();

    return (
      <div ref={ref} className={isPrint ? "px-6" : ""}>
        <div className="my-6">
          <UserInfo
            data={{
              class: data?.className,
              date: dayjs(data.startDatetime)
                .calendar("jalali")
                .format("YYYY/MM/DD"),
              examName: data.examName,
              examType: data.takingType,
              name: data.name,
              totalQuestions: data.questionNo,
              totalSubjects: data.moduleNo,
              school: data.schoolName,
              imageId: data.schoolLogoId ?? ""
            }}
          />
        </div>

        {isDesktop ? (
          <ToolCard
            title="کارنامه در یک نگاه"
            className="mb-6"
            tooltip={
              !isPrint && (
                <RTooltip trigger={<QuestionLinear />}>
                  <TableTooltip
                    title="راهنمای محاسبه شاخص‌ها"
                    items={SUMMARY_INFO_TOOLTIP_CONTENT}
                  />
                </RTooltip>
              )
            }
          >
            <SummaryInfo
              className="mt-4"
              mark={overalInfoJson?.mark}
              markWN={overalInfoJson?.markWN}
              corrects={overalInfoJson?.corrects}
              inCorrects={overalInfoJson?.inCorrects}
              noAnswers={overalInfoJson?.noAnswers}
              performanceLabel={overalInfoJson?.performanceLabel}
              answerRate={overalInfoJson?.answerRate}
              accuracyRate={overalInfoJson?.accuracyRate}
              subTitle=" (با نمره منفی)"
              isPrint={isPrint}
            />
          </ToolCard>
        ) : (
          <ToolCard
            title="کارنامه در یک نگاه"
            className="mb-3"
            tooltip={
              !isPrint && (
                <RTooltip trigger={<QuestionLinear />}>
                  <TableTooltip
                    title="راهنمای محاسبه شاخص‌ها"
                    items={SUMMARY_INFO_TOOLTIP_CONTENT}
                  />
                </RTooltip>
              )
            }
          >
            <GaugeChart
              className="mt-4"
              grainType={overalInfoJson?.grainType}
              grainId={overalInfoJson?.grainId}
              mark={overalInfoJson?.mark}
              markWN={overalInfoJson?.markWN}
              corrects={overalInfoJson?.corrects}
              inCorrects={overalInfoJson?.inCorrects}
              noAnswers={overalInfoJson?.noAnswers}
              performanceLabel={overalInfoJson?.performanceLabel}
              answerRate={overalInfoJson?.answerRate}
              accuracyRate={overalInfoJson?.accuracyRate}
              centerText="نمره نهایی (با نمره منفی)"
              showLabels={true}
            />
          </ToolCard>
        )}

        {isDesktop ? (
          <ToolCard
            title={getTableTitle()}
            actions={
              <>
                {!isSingleLesson && !isPrint ? (
                  <MultiLessonTabs
                    activeDetailsTabId={+activeDetailsTabId}
                    onChange={(id) => {
                      const nextParams = new URLSearchParams(searchParams);
                      nextParams.set(DETAILS_TAB_QUERY_KEY, String(id));
                      setSearchParams(nextParams, { replace: true });
                    }}
                  />
                ) : null}
              </>
            }
          >
            {+activeDetailsTabId === 1 && (
              <InfoDetailsTable
                data={transformExamDataToTableData(
                  (isSingleLesson ? chapterInfos : moduleInfos) ?? []
                )}
                enablePagination={false}
                enableSorting={true}
                enableFilters={false}
                isPrint={isPrint}
              />
            )}

            {+activeDetailsTabId === 2 && (
              <InfoDetailsTable
                data={transformExamDataToTableData(moduleGroupInfos ?? [])}
                enablePagination={false}
                enableSorting={true}
                enableFilters={false}
                isPrint={isPrint}
              />
            )}
          </ToolCard>
        ) : (
          <div className="flex flex-col gap-3 mb-6">
            <div>
              {!isSingleLesson ? (
                <Tabs
                  className="flex items-center bg-transparent !border-[#EEEEEE] !shadow-none h-10 px-1 gap-1"
                  activeTabId={activeDetailsTabId}
                  onChange={(id) => {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.set(DETAILS_TAB_QUERY_KEY, String(id));
                    setSearchParams(nextParams, { replace: true });
                  }}
                >
                  <Tab
                    id={1}
                    className="rounded-0 flex items-center justify-center !pb-0 h-9  px-6 py-1 text-sm text-neutral-600 after:!hidden overflow-hidden whitespace-nowrap"
                    classes={{
                      active: "!border-b !border-[#FF6A00] !text-[#FF6A00] pb-0"
                    }}
                  >
                    کتاب
                  </Tab>
                  <Tab
                    id={2}
                    className="rounded-0 flex items-center justify-center !pb-0 h-9  px-6 py-1 text-sm text-neutral-600 after:!hidden overflow-hidden whitespace-nowrap"
                    classes={{
                      active: "!border-b !border-[#FF6A00] !text-[#FF6A00] pb-0"
                    }}
                  >
                    گروه درسی
                  </Tab>
                </Tabs>
              ) : null}
            </div>

            <ToolCard title={getTableTitle()}>
              <div className="flex flex-col gap-3 mt-4">
                {(!isSingleLesson
                  ? (stackedSource ?? [])
                  : (chapterInfos ?? [])
                ).map((item) => (
                  <DetailsAccordion
                    defaultOpen={true}
                    key={item?.grainId}
                    title={item?.grainName ?? ""}
                    status={item?.performanceLabel}
                    items={[
                      {
                        title: "نمره نهایی",
                        value: `${(item?.mark ?? 0).toFixed(2).replace(".", "/")}%`
                      },
                      {
                        title: "نرخ پاسخ‌دهی",
                        value: `${item.answerRate.toFixed(2).replace(".", "/")}%`
                      },
                      {
                        title: "دقت پاسخ‌دهی",
                        value: `${(item?.accuracyRate ?? 0).toFixed(2).replace(".", "/")}%`
                      }
                    ]}
                    correct={item.corrects}
                    inCorrect={item.inCorrects}
                    total={item.corrects + item.inCorrects + item.noAnswers}
                    noAnswers={item.noAnswers}
                  />
                ))}
              </div>
            </ToolCard>
          </div>
        )}

        {activeDetailsTabId == 1 && isSingleLesson && (
          <ToolCard title="پراکندگی سطوح دشواری" className="my-6" collapsible>
            <>
              {isDesktop ? (
                <DifficultyTable
                  data={transformExamDataToDifficultyTableData(
                    difficultyLevelInfos ?? []
                  )}
                  enablePagination={false}
                  enableSorting={true}
                  enableFilters={false}
                  isPrint={isPrint}
                />
              ) : (
                <div className="flex flex-col gap-3 mt-4">
                  <SegmentedGauge segments={segmentedGaugeSegments} />

                  {difficultyLevelInfos?.map((item) => (
                    <DetailsBox
                      key={item?.levelName}
                      header={{
                        title: item.levelName,
                        value: `${item.corrects + item.inCorrects + item.noAnswers} سوال`
                      }}
                      withHeader
                      items={[
                        {
                          title: "نمره نهایی",
                          value: `${(item?.mark ?? 0).toFixed(2).replace(".", "/")}%`
                        },
                        {
                          title: "نرخ پاسخ‌دهی",
                          value: `${item.answerRate.toFixed(2).replace(".", "/")}%`
                        },
                        {
                          title: "دقت پاسخ‌دهی",
                          value: `${(item?.accuracyRate ?? 0).toFixed(2).replace(".", "/")}%`
                        }
                      ]}
                      correct={item.corrects}
                      inCorrect={item.inCorrects}
                      total={item.corrects + item.inCorrects + item.noAnswers}
                      noAnswers={item.noAnswers}
                    />
                  ))}
                </div>
              )}
            </>
          </ToolCard>
        )}

        <ToolCard
          title={getStackedBarTitle()}
          className="my-6"
          collapsible
          defaultOpen
        >
          <div className="w-full ">
            <HorizontalStackedBarChart
              data={stackedData}
              keys={["correct", "incorrect"]}
              colors={["#06B6D4", "#EF4444"]}
              title="نمرات به تفکیک موضوع"
              height={"auto"}
              colorAccessor={({ key, datum, defaultColor }) => {
                // Map by performance label if present; fallback to default color
                const label = (datum?.performanceLabel as string) ?? "نامشخص";
                const status = STATUS_CONFIG[label] || DEFAULT_STATUS;
                const normalize = (c: string) =>
                  c?.startsWith("#") ? c : `#${c}`;
                const hexToRgba = (hex: string, alpha: number) => {
                  const h = normalize(hex).replace("#", "");
                  if (h.length !== 6) return defaultColor;
                  const bigint = parseInt(h, 16);
                  const r = (bigint >> 16) & 255;
                  const g = (bigint >> 8) & 255;
                  const b = bigint & 255;
                  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                };
                // Use status color for "correct" and status backgroundColor for "incorrect"
                if (key === "correct") return normalize(status.color);
                if (key === "incorrect") return hexToRgba(status.color, 0.4);
                return defaultColor;
              }}
            />
          </div>
        </ToolCard>

        {!isSelfExam && (
          <ToolCard title="پیام استاد/مشاور">
            <MsgBox
              studentId={selectedStudentId}
              takingDetailId={examTakingId}
              comment={data.comment ?? ""}
            />
          </ToolCard>
        )}
      </div>
    );
  }
);

Type2.displayName = "Type2";

export default Type2;
